import { useState, useCallback } from "react";
import "@/App.css";
import SetupScreen from "@/components/SetupScreen";
import PlayScreen from "@/components/PlayScreen";
import SummaryScreen from "@/components/SummaryScreen";
import HistoryScreen from "@/components/HistoryScreen";
import { HOLE_COUNT } from "@/lib/game";
import { Toaster } from "@/components/ui/sonner";

function defaultCourse() {
    // Default: par 4 on every hole, handicap rating = hole number
    const pars = new Array(HOLE_COUNT).fill(4);
    const holeHandicapRatings = Array.from(
        { length: HOLE_COUNT },
        (_, i) => i + 1,
    );
    return { pars, holeHandicapRatings };
}

function App() {
    const [phase, setPhase] = useState("setup"); // "setup" | "play" | "summary" | "history"
    const [players, setPlayers] = useState([
        { name: "", handicap: 0 },
        { name: "", handicap: 0 },
    ]);
    const [course, setCourse] = useState(defaultCourse());
    const [roundId, setRoundId] = useState(null);
    // holes: array of length 18, each an object { gross: [], result: null | {...} }
    const [holes, setHoles] = useState(() =>
        Array.from({ length: HOLE_COUNT }, () => ({
            gross: [],
            result: null,
        })),
    );

    const startRound = useCallback((cfgPlayers, cfgCourse, existingRoundId = null) => {
        setPlayers(cfgPlayers);
        setCourse(cfgCourse);
        setHoles(
            Array.from({ length: HOLE_COUNT }, () => ({
                gross: new Array(cfgPlayers.length).fill(null),
                result: null,
            })),
        );
        setRoundId(existingRoundId || `round-${Date.now()}-${Math.floor(Math.random()*10000)}`);
        setPhase("play");
    }, [setPlayers, setCourse, setHoles, setRoundId, setPhase]);

    const resumeRound = useCallback((round) => {
        setPlayers(round.players);
        setCourse(round.course);
        setHoles(round.holes);
        setRoundId(round.id);
        setPhase("summary");
    }, [setPlayers, setCourse, setHoles, setRoundId, setPhase]);

    const resetGame = useCallback(() => {
        setPhase("setup");
        setPlayers([
            { name: "", handicap: 0 },
            { name: "", handicap: 0 },
        ]);
        setCourse(defaultCourse());
        setRoundId(null);
        setHoles(
            Array.from({ length: HOLE_COUNT }, () => ({
                gross: [],
                result: null,
            })),
        );
    }, [setPhase, setPlayers, setCourse, setRoundId, setHoles]);

    return (
        <div className="App" data-testid="app-root">
            <Toaster
                position="top-center"
                theme="dark"
                toastOptions={{
                    style: {
                        background: "#16181A",
                        color: "#FDFBF7",
                        border: "1px solid rgba(253,251,247,0.16)",
                        borderRadius: "3px",
                        fontFamily: "'IBM Plex Sans', sans-serif",
                    },
                }}
            />
            {phase === "setup" && <SetupScreen onStart={startRound} onHistory={() => { console.log("HISTORY CLICKED"); setPhase("history"); }} />}
            {phase === "history" && <HistoryScreen onBack={() => setPhase("setup")} onResume={resumeRound} />}
            {phase === "play" && (
                <PlayScreen
                    players={players}
                    course={course}
                    holes={holes}
                    setHoles={setHoles}
                    onFinish={() => setPhase("summary")}
                    onExit={resetGame}
                />
            )}
            {phase === "summary" && (
                <SummaryScreen
                    roundId={roundId}
                    players={players}
                    course={course}
                    holes={holes}
                    onNewRound={resetGame}
                    onBack={() => setPhase("play")}
                />
            )}
        </div>
    );
}

export default App;
